// Add-transcript confirm modal (design decision: drop/paste ALWAYS confirms here —
// never silently creates a meeting). Three sources feeding one shape: a dropped/
// picked text file, a Google Meet URL, or a Google Doc (Gemini notes) URL. Preview
// fills editable title/date/attendees; Ingest creates the meeting and fires the
// pipeline. visitId omitted (visits list) → the server auto-creates a remote visit.
import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Alert, CircularProgress, Chip,
} from "@mui/material";
import { Segmented } from "../../mui";
import { brainTokens } from "../../design/brainTheme";
import { previewMeetingImport, importMeeting, type ImportSource, type ImportPreview } from "../../lib/brain-api";

const c = brainTokens.color;

export function ImportTranscriptModal({ customerId, visitId, open, initialFile, onClose, onDone }: {
  customerId: string;
  visitId?: string;
  open: boolean;
  /** A file dropped on the page — pre-selects the file source and previews it. */
  initialFile?: File | null;
  onClose: () => void;
  onDone: (visitId: string) => void;
}) {
  const [source, setSource] = useState<ImportSource>("file");
  const [url, setUrl] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => { setSource("file"); setUrl(""); setFileName(null); setPreview(null); setError(null); setBusy(false); };

  const previewText = async (text: string) => {
    setBusy(true);
    setError(null);
    try {
      setPreview(await previewMeetingImport({ customerId, visitId, source: "file", transcriptText: text }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't read that transcript");
    } finally {
      setBusy(false);
    }
  };

  const previewFile = async (f: File) => {
    setFileName(f.name);
    await previewText(await f.text());
  };

  useEffect(() => {
    if (open && initialFile) void previewFile(initialFile);
    if (!open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFile]);

  const previewUrl = async () => {
    setBusy(true);
    setError(null);
    try {
      setPreview(await previewMeetingImport({ customerId, visitId, source, url }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't fetch that link");
    } finally {
      setBusy(false);
    }
  };

  const ingest = async () => {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const r = await importMeeting({
        customerId, visitId,
        title: preview.title, date: preview.date, attendees: preview.attendees,
        transcriptText: preview.transcriptText, googleSummaryText: preview.googleSummaryText,
      });
      onDone(r.visitId);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Add transcript</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <Segmented
          value={source}
          onChange={(s: ImportSource) => { setSource(s); setPreview(null); setError(null); }}
          options={[
            { key: "file", label: "Text file" },
            { key: "meet-url", label: "Meet link" },
            { key: "doc-url", label: "Google Doc" },
          ]}
        />
        {source === "file" ? (
          <Button variant="outlined" component="label" disabled={busy}>
            {fileName ?? "Choose a .txt / .md / .vtt file"}
            <input
              hidden type="file" accept=".txt,.md,.vtt,text/plain,text/markdown"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void previewFile(f); }}
            />
          </Button>
        ) : (
          <Box sx={{ display: "flex", gap: "8px" }}>
            <TextField
              size="small" fullWidth
              placeholder={source === "meet-url" ? "https://meet.google.com/xxx-xxxx-xxx" : "https://docs.google.com/document/d/…"}
              value={url} onChange={(e) => setUrl(e.target.value)}
            />
            <Button variant="outlined" disabled={busy || !url.trim()} onClick={() => void previewUrl()}>Fetch</Button>
          </Box>
        )}

        {busy && !preview && <CircularProgress size={22} sx={{ alignSelf: "center" }} />}
        {error && <Alert severity="error">{error}</Alert>}

        {preview && (
          <>
            <TextField label="Title" size="small" value={preview.title}
              onChange={(e) => setPreview({ ...preview, title: e.target.value })} />
            <TextField label="Date" size="small" value={preview.date}
              onChange={(e) => setPreview({ ...preview, date: e.target.value })} />
            <TextField label="Attendees (comma-separated)" size="small"
              value={preview.attendees.join(", ")}
              onChange={(e) => setPreview({ ...preview, attendees: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
            <Box sx={{ fontSize: 12.5, color: c.muted2, display: "flex", gap: "8px", alignItems: "center" }}>
              <Chip size="small" label={`${Math.round(preview.transcriptText.length / 1000)}k chars transcript`} />
              {preview.googleSummaryText && <Chip size="small" label="Google summary attached" />}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" disabled={busy || !preview} onClick={() => void ingest()}>
          {busy && preview ? "Ingesting…" : "Ingest"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
